import { Pill } from "@components";
import { IconButton } from "@controls";
import { ArrowRotateRightRegular, PlusRegular } from "@icons";
import Modal from "@src/components/modal";
import { Table as TanStackTable } from "@tanstack/react-table";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import TableActions from "./actions";
import TableColumnVisibilityToggle from "./column-visibility-toggle";
import { useTableContext } from "./context";
import TableFilterPopover, { TableFilterBar } from "./filter-v2";
import type { FilterMenuItem } from "./filter-v2/menu/types";
import { TableAction } from "./interface";

function TableHeading({
  headingLevel = 1,
  tableTitle,
  tableHeadingClassName = "text-xl font-semibold text-default",
}: {
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  tableTitle?: string;
  tableHeadingClassName?: string;
}) {
  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;
  return (
    <HeadingTag className={tableHeadingClassName}>{tableTitle}</HeadingTag>
  );
}

export interface FilterOption {
  label: string;
  type: "text";
  key: string;
}

interface TableControlsProps<T> {
  tableTitle?: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  tableHeadingClassName?: string;
  table: TanStackTable<T>;
  newRecordModalTitle?: string;
  newRecordModalDescription?: string;
  filterOptions: FilterOption[];
  filterMenuItemGroups?: FilterMenuItem[][];
  totalSelected: number;
  NewForm?: ({
    toggleOpen,
  }: {
    toggleOpen: (newOpen: boolean) => void;
  }) => JSX.Element;
  newFormLink?: string;
  refresh?: () => void;
  isRefreshing?: boolean;
  extraControls?: React.ReactNode;
  subheading?: React.ReactNode;
}

export default function TableControls<T>({
  tableTitle,
  table,
  newRecordModalTitle,
  newRecordModalDescription,
  totalSelected,
  refresh,
  isRefreshing = false,
  filterOptions,
  filterMenuItemGroups,
  NewForm,
  newFormLink,
  headingLevel = 1,
  tableHeadingClassName = "",
  extraControls = null,
  subheading = null,
}: TableControlsProps<T>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { filterableFields, baseFieldLabelTranslations } = useTableContext();

  const hasFilterMenu = filterMenuItemGroups && filterMenuItemGroups.length > 0;

  const filterOptionsInQueryParams = useMemo(() => {
    const res: any[] = [];

    for (const key of searchParams.keys()) {
      const value = searchParams.get(key);
      const filterOption = filterOptions.find((f) => f.key === key);

      if (filterOption) {
        res.push({
          value,
          ...filterOption,
        });
      }
    }

    for (const key of Object.keys(filterableFields ?? {})) {
      const filterFields = searchParams.get("filter");
      const filterFieldsDecoded = filterFields
        ? decodeURIComponent(filterFields)
        : "";
      const filterFieldsArray = filterFieldsDecoded.split("|");

      for (const field of filterFieldsArray) {
        const [fieldKey, value] = field.split(":");

        if (fieldKey === key) {
          res.push({
            key,
            value,
            label: t(`${baseFieldLabelTranslations}.${key}`),
          });
        }
      }
    }

    return res;
  }, [searchParams]);

  const actions: TableAction<T>[] = (
    table.options.meta && "actions" in table.options.meta
      ? table.options.meta["actions"]
      : []
  ) as TableAction<T>[];

  return (
    <div>
      <div className="flex min-h-target justify-between items-center px-2">
        <div>
          {!!tableTitle && (
            <TableHeading
              headingLevel={headingLevel}
              tableHeadingClassName={tableHeadingClassName}
              tableTitle={tableTitle}
            />
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-target-pitch-gap py-[7px]">
          {!!refresh && (
            <IconButton
              size="small"
              customIcon={
                <div
                  className={classNames({
                    "animate-spin text-muted cursor-wait": isRefreshing,
                  })}
                >
                  <ArrowRotateRightRegular />
                </div>
              }
              label={t("tableRefresh")}
              noTip
              disabled={isRefreshing}
              onClick={() => (refresh ? refresh() : () => {})}
            />
          )}
          <TableColumnVisibilityToggle />
          {hasFilterMenu && (
            <TableFilterPopover filterMenuItemGroups={filterMenuItemGroups} />
          )}
          {actions.length > 0 && <TableActions table={table} />}
          {extraControls}
          {!!NewForm && !newFormLink && (
            <Modal
              closeButtonVariant="legacy"
              open={newModalOpen}
              onOpenChange={setNewModalOpen}
              title={newRecordModalTitle}
              description={newRecordModalDescription}
              triggerAsChild={true}
              openTriggerChild={
                <IconButton
                  size="small"
                  customIcon={<PlusRegular />}
                  label={newRecordModalTitle ?? t("tableNewRecord")}
                  noTip
                />
              }
            >
              <NewForm toggleOpen={setNewModalOpen} />
            </Modal>
          )}
          {!!newFormLink && !NewForm && (
            <IconButton
              size="small"
              customIcon={<PlusRegular />}
              label={newRecordModalTitle ?? t("tableNewRecord")}
              noTip
              onClick={() => navigate(newFormLink)}
            />
          )}
        </div>
      </div>
      {hasFilterMenu && (
        <TableFilterBar filterMenuItemGroups={filterMenuItemGroups} />
      )}
      {subheading}
      {(!!filterOptionsInQueryParams.length || !!totalSelected) && (
        <div className="pb-1 flex gap-2 px-2">
          <span
            className={classNames("text-primary flex items-center", {
              hidden: !totalSelected,
            })}
          >
            {t("rowsSelectedVisible", { count: totalSelected })}
          </span>
          <div className="flex flex-row gap-1">
            {filterOptionsInQueryParams.map((option) => (
              <Pill key={option.key} className="bg-primary text-on-primary">
                <span>{`${option.label}: ${option.value}`}</span>
              </Pill>
            ))}
          </div>
          {!!filterOptionsInQueryParams.length && (
            <button
              className="relative hit-target text-primary border border-primary px-2 rounded-full text-sm lowercase"
              type="button"
              onClick={() => setSearchParams("")}
            >
              {t("clear")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
